import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Mail, RefreshCw, Loader2, ExternalLink, ChevronDown } from 'lucide-react';
import { useAuth } from '../../../contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_TTL_MS = 60_000; // 60s TTL — cheap re-open of the same tenant

// Module-scoped cache so re-mounting the card (tab switch) reuses the
// prior fetch. Key = `${domainId}:${days}`.
const _digestCache = new Map();

const RANGES = [
  { value: 7,  label: 'Last 7 days (real digest)' },
  { value: 14, label: 'Last 14 days' },
  { value: 30, label: 'Last 30 days' },
];

/**
 * DigestPreviewCard — inline preview of the Monday-morning manager digest
 * email. Master admins can flip through tenants; supervisors/managers see
 * their own tenant. No throttle, no send, just render the same helpers into
 * a sandboxed iframe.
 */
const DigestPreviewCard = ({ defaultDomainId = null }) => {
  const { user } = useAuth();
  const iframeRef = useRef(null);
  const [domains, setDomains] = useState([]);
  const [domainId, setDomainId] = useState(defaultDomainId || user?.domain_id || '');
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const [payload, setPayload] = useState(null);
  const [expanded, setExpanded] = useState(true);

  const isMaster = user?.role === 'master_admin';

  // Master admin needs the tenant list so they can flip domains.
  useEffect(() => {
    if (!isMaster) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/domains`);
        if (cancelled) return;
        const list = (r.data || []).map((d) => ({
          id: d.id, name: d.name || d.yabbit_domain || d.id.slice(0, 8),
        }));
        setDomains(list);
        if (!domainId && list.length > 0) setDomainId(list[0].id);
      } catch { /* silently degrade */ }
    })();
    return () => { cancelled = true; };
  }, [isMaster, domainId]);

  const load = useCallback(async (opts = {}) => {
    if (!expanded) return;
    // Supervisor: rely on backend to pick their own tenant. Master: needs id.
    if (isMaster && !domainId) return;
    const cacheKey = `${domainId || 'self'}:${days}`;
    if (!opts.force) {
      const cached = _digestCache.get(cacheKey);
      if (cached && (Date.now() - cached.at) < CACHE_TTL_MS) {
        setPayload(cached.data);
        return;
      }
    }
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (isMaster && domainId) params.set('domain_id', domainId);
      const r = await axios.get(`${API}/analytics/manager-digest/preview?${params}`);
      _digestCache.set(cacheKey, { data: r.data, at: Date.now() });
      setPayload(r.data || null);
    } catch (e) {
      const msg = e?.response?.data?.detail || 'Could not load digest preview';
      toast.error(msg);
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [domainId, days, isMaster, expanded]);

  useEffect(() => { load(); }, [load]);

  // Push HTML into the iframe once we have it. Using srcdoc keeps the
  // digest visually identical to the real inbox render, sandboxed away
  // from our app's styles. When the payload clears (error path), wipe the
  // iframe so a failed switch to another tenant doesn't leave the
  // previous tenant's digest rendered as if it were the current view.
  useEffect(() => {
    if (!iframeRef.current) return;
    iframeRef.current.srcdoc = payload?.html || '';
  }, [payload]);

  return (
    <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden" data-testid="digest-preview-card">
      <button
        type="button"
        onClick={() => setExpanded((p) => !p)}
        className="w-full px-5 py-3.5 bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-between"
        data-force-dark="true"
        data-testid="digest-preview-toggle"
      >
        <div className="flex items-center gap-3 text-left">
          <Mail className="w-5 h-5 text-teal-300" />
          <div>
            <p className="font-bold text-sm" style={{ fontFamily: 'Outfit' }}>Manager digest preview</p>
            <p className="text-[11px] text-slate-300">
              What lands in every manager&apos;s inbox on Monday — no send, no throttle
            </p>
          </div>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <>
          <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex flex-wrap items-center gap-3 text-xs">
            {isMaster && (
              <label className="inline-flex items-center gap-1.5">
                <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Tenant</span>
                <select
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                  className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                  data-testid="digest-preview-tenant"
                >
                  {domains.map((d) => (
                    <option key={d.id} value={d.id}>{d.name}</option>
                  ))}
                </select>
              </label>
            )}
            <label className="inline-flex items-center gap-1.5">
              <span className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider">Window</span>
              <select
                value={days}
                onChange={(e) => setDays(Number(e.target.value))}
                className="text-xs border border-slate-200 rounded-md px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-teal-400"
                data-testid="digest-preview-days"
              >
                {RANGES.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            <button
              onClick={() => load({ force: true })}
              disabled={loading}
              className="ml-auto text-xs font-semibold text-slate-600 border border-slate-200 px-3 py-1 rounded-lg hover:bg-white inline-flex items-center gap-1.5 disabled:opacity-50"
              data-testid="digest-preview-refresh"
            >
              {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
              Refresh
            </button>
            {payload?.html && (
              <button
                onClick={() => {
                  const w = window.open('', '_blank');
                  if (w) { w.document.write(payload.html); w.document.close(); }
                }}
                className="text-xs font-semibold text-teal-700 border border-teal-200 px-3 py-1 rounded-lg hover:bg-teal-50 inline-flex items-center gap-1.5"
                data-testid="digest-preview-popout"
              >
                <ExternalLink className="w-3 h-3" /> Full window
              </button>
            )}
          </div>

          {payload?.telemetry && (
            <div className="px-5 py-2 text-[11px] text-slate-500 bg-slate-50 border-b border-slate-100 flex flex-wrap gap-x-4 gap-y-1">
              <span><b className="text-slate-800">{payload.telemetry.calls}</b> calls</span>
              <span><b className="text-teal-700">{payload.telemetry.revivable_rows}</b> revivable</span>
              <span><b className="text-rose-700">{payload.telemetry.aging_rows}</b> aging</span>
              <span><b className="text-emerald-700">{payload.telemetry.rescued_rows}</b> rescued</span>
              <span><b className="text-violet-700">{payload.telemetry.bulk_actions}</b> bulk-action batch{payload.telemetry.bulk_actions === 1 ? '' : 'es'}</span>
            </div>
          )}

          <div className="p-4 bg-slate-100" style={{ minHeight: 520 }}>
            {loading && !payload ? (
              <div className="flex items-center justify-center h-full py-20 text-slate-400 text-xs gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Building digest…
              </div>
            ) : payload?.html ? (
              <iframe
                ref={iframeRef}
                title="Manager digest preview"
                className="w-full bg-white rounded-lg border border-slate-200"
                style={{ height: 800 }}
                // Sandbox is intentionally narrow — the digest HTML is
                // server-composed by our own template so allowing popups
                // is safe. Without `allow-popups` + `allow-popups-to-
                // escape-sandbox` the "Open call →" links (target=_blank)
                // silently no-op, which is the "compliance alert phone
                // call not clickable" bug reported Feb 2026.
                sandbox="allow-popups allow-popups-to-escape-sandbox"
                data-testid="digest-preview-iframe"
              />
            ) : (
              <div className="text-center py-20 text-xs text-slate-400">
                No digest data yet.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default DigestPreviewCard;
